import { faUserPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useQueryCache } from "react-query";
import { Link, useParams } from "react-router-dom";
import { Button, Checkbox, Container, Header, Loader, Message, Segment, Table } from "semantic-ui-react";
import DashboardPreview from "../../../../../../apps/pages/appPreviews/DashboardPreview";
import NotificationPreview from "../../../../../../apps/pages/appPreviews/NotificationPreview";
import { ParsedPhoneNumber } from "../../../../../../components/global_components/form_components/ControlledPhoneNumber";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useNotifyAppDownloadLinks } from "../../../../hooks/useNotifyAppDownloadLinks";
import { useNotifySettingsPublic } from "../../../../hooks/useNotifySettingsPublic";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { useNotifySubscriptionGroupsPublic } from "../../../../hooks/useNotifySubscriptionGroupsPublic";
import { NotifyPublicTopNavigation } from "../components/NotifyPublicTopNavigation";
import { ManageEmailOrPhoneNumberCard } from "./components/ManageEmailOrPhoneNumberCard";
import { SignUpModal } from "./components/modals/SignUpModal";
import { uniqBy } from "lodash";
import { useNotifySubscriptionPreferenceUpdate } from "../../../../hooks/useNotifySubscriptionPreferenceUpdate";

export function PublicSubscriptionGroupsPage() {
  const { site } = useParams<{ site: string }>();

  const [signUpModalData, setSignUpModalData] = useState<{ type: "Email" | "Phone" } | null>(null);

  const { subscriberContactMethods, isSubscriberContactMethodsLoading, isLoggedIn, isLoggedInAnonymously } =
    useNotifySubscriberContactMethodsForLoggedInUser();
  const subscriberContactMethod = subscriberContactMethods?.at(0);

  const { downloadLinks, isDownloadLinksLoading } = useNotifyAppDownloadLinks();
  const { isConfigured, isLoading, publicNotifySettings } = useNotifySettingsPublic();
  const { publicSubscriptionGroups, isPublicSubscriptionGroupsLoading } = useNotifySubscriptionGroupsPublic();

  const subscriptionGroups = uniqBy(
    [...(publicSubscriptionGroups ?? []), ...(subscriberContactMethod?.subscriptionGroups ?? [])],
    "id",
  );

  const devicePreviewIsLoaded = useQueryHasData(["citizen.site", site]);
  const delayedIsDevicePreviewLoading = useDebounce(!devicePreviewIsLoaded, 250);

  const isPageLoading =
    isDownloadLinksLoading || isLoading || isPublicSubscriptionGroupsLoading || isSubscriberContactMethodsLoading;

  const hasSubscriptionGroups = publicSubscriptionGroups && publicSubscriptionGroups?.length > 0;

  return (
    <>
      <SignUpModal data={signUpModalData} onClose={() => setSignUpModalData(null)} />
      <Loader
        className={classNames({
          "tw-hidden": !isPageLoading,
        })}
        active
      >
        Loading...
      </Loader>
      <div
        className={classNames("tw-mb-24", {
          "tw-hidden": isPageLoading,
        })}
      >
        <NotifyPublicTopNavigation />
        <Container>
          <Header as='h1' className='tw-mb-3 tw-mt-6 md:tw-my-10'>
            Manage Your Notifications
          </Header>
          <div className='tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row'>
            {!isLoggedIn && <ManageEmailOrPhoneNumberCard />}
            <Segment className='tw-h-42 tw-m-0 tw-flex tw-w-full tw-justify-between tw-gap-3 tw-space-y-1 tw-p-5'>
              <div>
                <Header as='h2' className='tw-m-0 tw-text-lg'>
                  Download the App
                </Header>
                <p>Push notifications will be sent directly to your phone.</p>
                <div className='tw-flex tw-gap-2'>
                  <a href={downloadLinks?.appleLink ?? ""}>
                    <img
                      src='https://assets.govoutreach.com/static/ios-appstore-badge-black-2025.png'
                      alt='App Store'
                      className='tw-h-8'
                    />
                  </a>
                  <a href={downloadLinks?.googleLink ?? ""}>
                    <img
                      src='https://assets.govoutreach.com/static/google-play-badge-2025.png'
                      alt='Google Play'
                      className='tw-h-8'
                    />
                  </a>
                </div>
              </div>
              <div className='tw-relative tw-hidden xs:tw-block'>
                <Loader active={delayedIsDevicePreviewLoading} size='small' />
                <div
                  className={classNames(
                    "tw-transition-opacity tw-duration-100",
                    delayedIsDevicePreviewLoading ? "tw-opacity-0" : "tw-opacity-100",
                  )}
                >
                  <img
                    src='/assets/notify/device-frames.png'
                    alt='App Examples'
                    className='tw-relative tw-z-30 tw-max-w-[7rem]'
                  />
                  <div className='tw-absolute tw-right-[-182.5px] tw-top-[-488px] tw-z-20 tw-h-[1100px] tw-w-[500px] tw-scale-[10.7%] tw-select-none tw-overflow-hidden tw-rounded-[50px] tw-bg-white'>
                    <DashboardPreview />
                  </div>
                  <div className='tw-absolute tw-right-[-229px] tw-top-[-487.5px] tw-z-10 tw-h-[1100px] tw-w-[500px] tw-scale-[8%] tw-select-none tw-overflow-hidden tw-rounded-[50px] tw-bg-white'>
                    <NotificationPreview />
                  </div>
                </div>
              </div>
            </Segment>
            {isConfigured && (
              <Segment className='tw-h-42 tw-m-0 tw-flex tw-w-full tw-justify-between tw-gap-3 tw-space-y-1 tw-p-5'>
                <div className='tw-space-y-1'>
                  <Header as='h2' className='tw-m-0 tw-text-lg'>
                    <ParsedPhoneNumber value={publicNotifySettings?.sitePhoneNumber} />
                  </Header>
                  <p className='tw-m-0'>
                    Text{" "}
                    <b>
                      {'"'}JOIN{'"'}
                    </b>{" "}
                    or <b>Call</b> for updates
                  </p>
                  <div className='tw-flex tw-gap-2'>
                    <Button
                      size='tiny'
                      secondary
                      className='tw-m-0'
                      as='a'
                      href={`sms:${publicNotifySettings?.sitePhoneNumber}?body=JOIN`}
                    >
                      Text {'"'}JOIN{'"'}
                    </Button>
                    <Button
                      size='tiny'
                      secondary
                      className='tw-m-0'
                      as='a'
                      href={`tel:${publicNotifySettings?.sitePhoneNumber}`}
                    >
                      Call
                    </Button>
                  </div>
                </div>
                <div className='tw-hidden xs:tw-block'>
                  <img src='/assets/notify/text-preview.png' alt='SMS Preview' className='tw-max-w-[11rem]' />
                </div>
              </Segment>
            )}
          </div>
          {!isLoggedIn && (
            <div>
              <Header as='h2' className='tw-mb-3 tw-mt-6 md:tw-mt-10'>
                Subscription Groups
              </Header>
              {isLoggedInAnonymously && (
                <Message info className='tw-flex tw-items-center tw-gap-5'>
                  <FontAwesomeIcon icon={faUserPlus} size='xl' />
                  <div>
                    <Message.Header>Sign Up to Better Manage Your Notifications</Message.Header>
                    <Message.Content>
                      <p>Add multiple contact methods, connect the mobile app, and more.</p>
                      <Button
                        primary
                        as={Link}
                        to={{
                          pathname: `/${site}/public/register`,
                          search: `?redirect=/${site}/public/notify`,
                        }}
                      >
                        Sign Up
                      </Button>
                    </Message.Content>
                  </div>
                </Message>
              )}
              {!hasSubscriptionGroups && (
                <Message>
                  <Message.Header>No Subscription Groups</Message.Header>
                  <p>There are no subscription groups available for you to subscribe to.</p>
                </Message>
              )}
              {hasSubscriptionGroups && (
                <div>
                  {/* Display table when screen is medium or larger */}
                  <Table className='tw-hidden md:tw-table'>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Group</Table.HeaderCell>
                        {isConfigured && <Table.HeaderCell className='tw-w-36'>SMS & Voice</Table.HeaderCell>}
                        <Table.HeaderCell className='tw-w-36'>Email</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {subscriptionGroups?.map((group) => (
                        <Table.Row key={group.id}>
                          <Table.Cell>
                            <Header as='h4' className='tw-m-0 tw-text-base'>
                              {group.name}
                            </Header>
                            <p className='tw-m-0 tw-text-black-light'>{group.description}</p>
                          </Table.Cell>
                          <Table.Cell>
                            {isConfigured && (
                              <PreferenceCheckbox groupId={group.id} type='Phone' onOpenModal={setSignUpModalData} />
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            <PreferenceCheckbox groupId={group.id} type='Email' onOpenModal={setSignUpModalData} />
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  {/* Display cards when screen is smaller than medium */}
                  <div className='tw-space-y-2 md:tw-hidden'>
                    {subscriptionGroups?.map((group) => (
                      <Segment key={group.id} className='tw-m-0 tw-flex tw-flex-col tw-gap-3'>
                        <div className='tw-space-y-1'>
                          <Header as='h3' className='tw-m-0 tw-text-lg'>
                            {group.name}
                          </Header>
                          <p className='tw-m-0'>{group.description}</p>
                        </div>
                        <div className='tw-flex tw-flex-col tw-gap-2'>
                          {isConfigured && (
                            <PreferenceCheckbox
                              groupId={group.id}
                              label='SMS & Voice'
                              type='Phone'
                              onOpenModal={setSignUpModalData}
                            />
                          )}
                          <PreferenceCheckbox
                            groupId={group.id}
                            label='Email'
                            type='Email'
                            onOpenModal={setSignUpModalData}
                          />
                        </div>
                      </Segment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Container>
      </div>
    </>
  );
}

function PreferenceCheckbox({
  groupId,
  label,
  type,
  onOpenModal,
}: {
  groupId: number;
  label?: string;
  type: "Email" | "Phone";
  onOpenModal: (data: { type: "Email" | "Phone" }) => void;
}) {
  const { subscriberContactMethods, isLoggedInAnonymously } = useNotifySubscriberContactMethodsForLoggedInUser();
  const subscriberContactMethod = subscriberContactMethods?.at(0);
  const { editSubscriptionPreference, isEditSubscriptionPreferenceLoading } = useNotifySubscriptionPreferenceUpdate();

  const onEmailCheckboxClick = async () => {
    if (!isLoggedInAnonymously || subscriberContactMethod?.type === "Phone") {
      onOpenModal({ type: "Email" });
      return;
    }

    await toggleSubscriptionPreference();
  };

  const onSMSCheckboxClick = async () => {
    if (!isLoggedInAnonymously || subscriberContactMethod?.type === "Email") {
      onOpenModal({ type: "Phone" });
      return;
    }

    await toggleSubscriptionPreference();
  };

  const toggleSubscriptionPreference = async () => {
    if (!subscriberContactMethod) return;

    const existingGroup = subscriberContactMethod.subscriptionGroups.find((group) => group.id === groupId);
    const toggledType = existingGroup ? "Unsubscribe" : "Subscribe";

    await editSubscriptionPreference({
      subscriptionGroupId: groupId,
      subscriberContactMethodId: subscriberContactMethod.id,
      type: toggledType,
    });
  };

  const isEmailCheckboxChecked = () => {
    if (!isLoggedInAnonymously || subscriberContactMethod?.type === "Phone") return false;
    if (!subscriberContactMethod) return false;
    return subscriberContactMethod?.subscriptionGroups.some((group) => group.id === groupId);
  };

  const isSMSCheckboxChecked = () => {
    if (!isLoggedInAnonymously || subscriberContactMethod?.type === "Email") return false;
    if (!subscriberContactMethod) return false;
    return subscriberContactMethod?.subscriptionGroups.some((group) => group.id === groupId);
  };

  const isCheckboxChecked = type === "Email" ? isEmailCheckboxChecked() : isSMSCheckboxChecked();
  const onCheckboxClick = type === "Email" ? onEmailCheckboxClick : onSMSCheckboxClick;

  return (
    <div className='tw-flex tw-items-center tw-gap-3'>
      <Checkbox
        label={label}
        toggle
        checked={isCheckboxChecked}
        onChange={onCheckboxClick}
        disabled={isEditSubscriptionPreferenceLoading}
      />
      <Loader active={isEditSubscriptionPreferenceLoading} size='tiny' inline />
    </div>
  );
}

function useQueryHasData(queryKey: any) {
  const queryCache = useQueryCache();
  const query = queryCache.getQuery(queryKey);

  const [hasData, setHasData] = useState(!!query?.state.data);

  useEffect(() => {
    const unsubscribe = queryCache.subscribe((_, query) => {
      const keyMatches = JSON.stringify(query?.queryKey) === JSON.stringify(queryKey);
      if (keyMatches && !!query?.state.data) setHasData(true);
    });

    return unsubscribe;
  }, [queryKey, queryCache]);

  return hasData;
}
